import type { ISODate, ISODateTime } from "./types";

/**
 * Date helpers that work on plain `YYYY-MM-DD` strings.
 * Arithmetic runs in UTC so daylight-saving never shifts a date. The team works in WIB
 * (UTC+7, no DST), so "today", clock times, and greetings are read in WIB on every device —
 * a member travelling abroad still sees the same dates and the same "07:30 WIB".
 */

const DAY_MS = 86_400_000;
/** Western Indonesian Time: fixed UTC+7. */
export const WIB_OFFSET_MS = 7 * 3_600_000;
/** A Date whose UTC fields read as the WIB wall clock. */
const wib = (date: Date) => new Date(date.getTime() + WIB_OFFSET_MS);

export const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;
export const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
export const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;
export const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

const pad = (n: number) => String(n).padStart(2, "0");

function toUtcMs(iso: ISODate): number {
  const [y, m, d] = iso.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
}

function fromUtcMs(ms: number): ISODate {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

export function isISODate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/** Local calendar date of a Date (defaults to now). */
/** Calendar date (WIB) of an instant. */
export function toISODate(date: Date = new Date()): ISODate {
  const w = wib(date);
  return `${w.getUTCFullYear()}-${pad(w.getUTCMonth() + 1)}-${pad(w.getUTCDate())}`;
}

export const todayISO = (now: Date = new Date()) => toISODate(now);

export function addDays(iso: ISODate, days: number): ISODate {
  return fromUtcMs(toUtcMs(iso) + days * DAY_MS);
}

export const addWeeks = (iso: ISODate, weeks: number) => addDays(iso, weeks * 7);

/** Whole days from `b` to `a` (positive when `a` is later). */
export function diffDays(a: ISODate, b: ISODate): number {
  return Math.round((toUtcMs(a) - toUtcMs(b)) / DAY_MS);
}

export function weekdayOf(iso: ISODate): number {
  return new Date(toUtcMs(iso)).getUTCDay();
}

export function isWeekday(iso: ISODate, weekday: number): boolean {
  return weekdayOf(iso) === weekday;
}

/** The session day on or after `iso`. */
export function weekdayOnOrAfter(iso: ISODate, weekday: number): ISODate {
  const delta = (weekday - weekdayOf(iso) + 7) % 7;
  return addDays(iso, delta);
}

/** The session day on or before `iso`. */
export function weekdayOnOrBefore(iso: ISODate, weekday: number): ISODate {
  const delta = (weekdayOf(iso) - weekday + 7) % 7;
  return addDays(iso, -delta);
}

/** `count` consecutive session days starting from the first one on/after `from`. */
export function upcomingWeekdays(from: ISODate, weekday: number, count: number): ISODate[] {
  const first = weekdayOnOrAfter(from, weekday);
  return Array.from({ length: count }, (_, i) => addWeeks(first, i));
}

export function monthKey(iso: ISODate): string {
  return iso.slice(0, 7);
}

export function startOfMonth(iso: ISODate): ISODate {
  return `${iso.slice(0, 7)}-01`;
}

export function addMonths(iso: ISODate, months: number): ISODate {
  const [y, m] = iso.split("-").map(Number);
  const total = y * 12 + (m - 1) + months;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return `${ny}-${pad(nm)}-01`;
}

export function daysInMonth(iso: ISODate): number {
  const [y, m] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** Monday-first grid of 6 weeks (42 days) covering the month of `iso`. */
export function monthGrid(iso: ISODate): ISODate[] {
  const first = startOfMonth(iso);
  const lead = (weekdayOf(first) + 6) % 7; // days since Monday
  const start = addDays(first, -lead);
  const weeks = Math.ceil((lead + daysInMonth(iso)) / 7);
  return Array.from({ length: weeks * 7 }, (_, i) => addDays(start, i));
}

export type DateStyle = "full" | "long" | "medium" | "short" | "dayMonth" | "monthYear" | "weekday" | "monthShort" | "day";

/** English formats (decision #5: whole UI in English). */
export function formatDate(iso: ISODate, style: DateStyle = "medium"): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const wd = weekdayOf(iso);
  switch (style) {
    case "full":
      return `${WEEKDAYS[wd]}, ${d} ${MONTHS[m - 1]} ${y}`;
    case "long":
      return `${WEEKDAYS[wd]}, ${d} ${MONTHS_SHORT[m - 1]} ${y}`;
    case "medium":
      return `${WEEKDAYS_SHORT[wd]}, ${d} ${MONTHS_SHORT[m - 1]} ${y}`;
    case "short":
      return `${d} ${MONTHS_SHORT[m - 1]} ${y}`;
    case "dayMonth":
      return `${d} ${MONTHS_SHORT[m - 1]}`;
    case "monthYear":
      return `${MONTHS[m - 1]} ${y}`;
    case "monthShort":
      return `${MONTHS_SHORT[m - 1]} ${String(y).slice(2)}`;
    case "weekday":
      return WEEKDAYS[wd];
    case "day":
      return String(d);
  }
}

/** "Today", "Tomorrow", "in 5 days", "Yesterday", "3 days ago". */
export function countdownLabel(iso: ISODate, today: ISODate): string {
  const diff = diffDays(iso, today);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  if (diff > 1) return `in ${diff} days`;
  return `${Math.abs(diff)} days ago`;
}

export function formatTime(at: ISODateTime): string {
  const w = wib(new Date(at));
  return `${pad(w.getUTCHours())}:${pad(w.getUTCMinutes())}`;
}

/** "just now", "5 min ago", "2 hours ago", "3 days ago", or a short date. */
export function relativeTime(at: ISODateTime, now: Date = new Date()): string {
  const diffMs = now.getTime() - new Date(at).getTime();
  const min = Math.round(diffMs / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const hours = Math.round(min / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return formatDate(toISODate(new Date(at)), "short");
}

/** Group heading for timelines: "Today", "Yesterday", or the full date. */
export function dayGroupLabel(at: ISODateTime, today: ISODate): string {
  const iso = toISODate(new Date(at));
  const diff = diffDays(today, iso);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return formatDate(iso, "long");
}

/** Build an ISO timestamp for a WIB date + "HH:mm" (WIB). */
export function atTime(iso: ISODate, time: string): ISODateTime {
  const [y, m, d] = iso.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  return new Date(Date.UTC(y, m - 1, d, hh, mm) - WIB_OFFSET_MS).toISOString();
}

export function greeting(now: Date = new Date()): string {
  const h = wib(now).getUTCHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}
